import { isPast } from 'date-fns';
import { FormattedMessage, useIntl } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import type { Sport } from '#src/__generated__/globalTypes.ts';
import { PromotionalEventCompletionState } from '#src/__generated__/globalTypes.ts';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyS, BrandS, LabelM, LabelS } from '#src/atoms/typography/index.tsx';
import { ActivePromotionalEventTimeLeft } from '#src/components/wallet/BankEthAccounting/AddFundsToFiatWallet/promo/ActivePromotionalEventTimeLeft/index.tsx';
import { Cta } from '#src/components/wallet/BankEthAccounting/AddFundsToFiatWallet/promo/Cta/index.tsx';
import { useCCRewardRange } from '#src/components/wallet/BankEthAccounting/AddFundsToFiatWallet/promo/Details/index.tsx';
import { Remaining } from '#src/components/wallet/BankEthAccounting/AddFundsToFiatWallet/promo/Remaining/index.tsx';
import { SpentProgress } from '#src/components/wallet/BankEthAccounting/AddFundsToFiatWallet/promo/SpentProgress/index.tsx';
import type { ConfigQuery } from '#src/contexts/config/types.ts';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useSportContext } from '#src/contexts/sport/index.ts';
import { useAmountWithConversion } from '#src/hooks/useAmountWithConversion.ts';
import { useMonetaryAmount } from '#src/hooks/useMonetaryAmount.ts';
import { useFiatBalance } from '#src/hooks/wallets/useFiatBalance.ts';
import { isType } from '#src/lib/gql/index.ts';
import { MonetaryAmount } from '#src/lib/monetaryAmount.ts';
import { getFormattedSport } from '#src/lib/sports.ts';

const Root = styled(Vertical).attrs({ center: true })`
  text-align: center;
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  color: var(--c-white);
  border: 1px solid;
  background:
    linear-gradient(0deg, var(--c-nd-50), var(--c-nd-50)),
    linear-gradient(0deg, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)),
    var(--bg) center center no-repeat;
  background-size: cover;
  background-position: top;
`;

const StyledLink = styled(Link)`
  color: var(--c-nd-600);
  text-decoration: underline;
`;

const Rules = ({
  probableRewardConfigs,
}: {
  probableRewardConfigs: NonNullable<
    NonNullable<ConfigQuery['currentUser']>['completablePromotionalEvent']
  >['probableRewardConfigs'];
}) => {
  const getCCRewardRange = useCCRewardRange();
  const { currentUser } = useCurrentUserContext();
  const { fiatCurrency } = useFiatBalance();
  const { formatNumber } = useIntl();
  const ccRange = getCCRewardRange(probableRewardConfigs);

  const { max, min } = ccRange || {};

  const { main } = useAmountWithConversion({
    monetaryAmount: min || MonetaryAmount.zero,
  });
  const { main: mainMax } = useAmountWithConversion({
    monetaryAmount: max || MonetaryAmount.zero,
  });
  if (!currentUser?.completablePromotionalEvent) return null;

  if (!ccRange) return null;

  return (
    <FormattedMessage
      defaultMessage="Deposit {amount}+ for a guaranteed {min}-{max} in market credits!"
      values={{
        amount: formatNumber(
          (currentUser.completablePromotionalEvent.eligibilityRules
            .minimumDepositAmount || 0) / 100,
          {
            style: 'currency',
            currency: fiatCurrency,
            minimumFractionDigits: 0,
          }
        ),
        min: main,
        max: mainMax,
      }}
    />
  );
};

const Title = ({
  completablePromotionalEvent,
  sport,
}: {
  completablePromotionalEvent: NonNullable<
    NonNullable<ConfigQuery['currentUser']>['completablePromotionalEvent']
  >;
  sport: Sport;
}) => {
  const { toMonetaryAmount } = useMonetaryAmount();
  const monetaryTargetAmount = toMonetaryAmount(
    completablePromotionalEvent.promotionalEventCompletion?.targetAmount ||
      MonetaryAmount.zero
  );
  const { main } = useAmountWithConversion({
    monetaryAmount:
      completablePromotionalEvent.promotionalEventCompletion?.targetAmount ||
      MonetaryAmount.zero,
  });
  const getCCRewardRange = useCCRewardRange();
  const ccRange = getCCRewardRange(
    completablePromotionalEvent.probableRewardConfigs
  );

  const { main: rewardAmount } = useAmountWithConversion({
    monetaryAmount: ccRange?.min || MonetaryAmount.zero,
  });

  const formattedSport = getFormattedSport(sport);

  if (
    completablePromotionalEvent.completionRules.completion_percentage &&
    completablePromotionalEvent.completionRules.completion_percentage !== 100 &&
    isType(completablePromotionalEvent, 'DepositPromotionalEvent')
  ) {
    return (
      <FormattedMessage
        defaultMessage="Spend at least {percentage, number, ::percent} in {sport, select, undefined {} other {{sport} }}Instant Buy or Auctions to get your reward."
        values={{
          percentage:
            completablePromotionalEvent.completionRules.completion_percentage /
            100,
          sport: formattedSport,
        }}
      />
    );
  }
  if (completablePromotionalEvent.promotionalEventCompletion) {
    if (ccRange?.min.isEqualTo(ccRange.max)) {
      return (
        <FormattedMessage
          defaultMessage="Spend at least {amount} in {sport, select, undefined {} other {{sport} }}Instant Buy or Auctions to get {ccAmount} in market credits."
          values={{
            amount: main,
            ccAmount: rewardAmount,
            sport: formattedSport,
          }}
        />
      );
    }
    if (monetaryTargetAmount.isZero) {
      return (
        <FormattedMessage
          defaultMessage="Buy a card in {sport, select, undefined {} other {{sport} }} on Instant Buy or Auctions"
          values={{
            value: main,
            sport: formattedSport,
          }}
        />
      );
    }

    return (
      <FormattedMessage
        defaultMessage="Spend at least {amount} in {sport, select, undefined {} other {{sport} }}Instant Buy or Auctions to get your reward."
        values={{
          amount: main,
          sport: formattedSport,
        }}
      />
    );
  }
  return null;
};

export const CompactBanner = ({
  showDepositCta,
  showInprogress,
}: {
  showInprogress?: boolean;
  showDepositCta?: boolean;
}) => {
  const { currentUser } = useCurrentUserContext();
  const { sport } = useSportContext();
  if (!currentUser) return null;

  const { completablePromotionalEvent } = currentUser;

  if (!completablePromotionalEvent) return null;

  const {
    promotionalEventCompletion,
    sport: completablePromotionalEventSport,
  } = completablePromotionalEvent;

  if (completablePromotionalEventSport !== sport) return null;

  // Case promo is in progress
  if (promotionalEventCompletion) {
    if (
      showInprogress &&
      promotionalEventCompletion.aasmState ===
        PromotionalEventCompletionState.PENDING &&
      !isPast(promotionalEventCompletion.expiresAt)
    ) {
      return (
        <Root
          style={
            {
              '--bg': `url(${completablePromotionalEvent.pictureUrl})`,
            } as CSSProperties
          }
        >
          <Horizontal>
            <ActivePromotionalEventTimeLeft />
          </Horizontal>
          <BrandS bold>{completablePromotionalEvent.name}</BrandS>
          <BodyS>
            <Title
              completablePromotionalEvent={completablePromotionalEvent}
              sport={completablePromotionalEventSport}
            />
          </BodyS>

          <SpentProgress />
        </Root>
      );
    }
    return null;
  }

  if (
    !completablePromotionalEvent ||
    isPast(completablePromotionalEvent.endDate)
  ) {
    return null;
  }

  const hasMaximumNumberOfParticipants =
    isType(completablePromotionalEvent, 'DepositPromotionalEvent') &&
    !!completablePromotionalEvent.completionRules
      .maximum_number_of_participants;

  return (
    <Root
      style={
        {
          '--bg': `url(${completablePromotionalEvent.pictureUrl})`,
        } as CSSProperties
      }
    >
      <BrandS bold>{completablePromotionalEvent.name}</BrandS>
      <LabelM color="var(--c-nd-600)">
        {completablePromotionalEvent.description}
      </LabelM>
      {!!hasMaximumNumberOfParticipants && (
        <Remaining
          completedParticipants={
            completablePromotionalEvent.completedParticipants
          }
          maximumNumberOfParticipants={
            completablePromotionalEvent.completionRules
              .maximum_number_of_participants
          }
        />
      )}
      <LabelS bold>
        {completablePromotionalEvent.marketBannerTitle || (
          <Rules
            probableRewardConfigs={
              completablePromotionalEvent.probableRewardConfigs
            }
          />
        )}{' '}
        <StyledLink
          to={completablePromotionalEvent.blogUrl || ''}
          target="_blank"
          rel="noopener noreferrer"
        >
          <FormattedMessage defaultMessage="More details" />
        </StyledLink>
      </LabelS>
      {!!showDepositCta && <Cta color="quaternary" />}
    </Root>
  );
};
